import { css } from "@emotion/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Grid as GridType, GridStatus, GridValues } from "@/types/grid";
import { GridDisplay, type CellErrors } from "./GridDisplay";
import { Button } from "./button";
import { trpcClient } from "@/lib/trpc";
import {
  bingoGrid,
  bingoRow,
  bingoCell,
  bingoCellEmpty,
  bingoGridOverlay,
  bingoGridOverlayBlur,
  bingoGridOverlayDim,
  bingoGridValidateBtn,
  validateGridModalOverlay,
  validateGridModal,
  validateGridModalTitle,
  validateGridModalContent,
  validateGridModalPhoto,
  validateGridModalImg,
  validateGridModalGrid,
  validateGridModalErrors,
  validateGridModalActions,
} from "@/styles/bingo-grid";

const IMAGE_BASE_URL = process.env.BUN_PUBLIC_CDN_URL;

const validateGridModalGridInner = css`
  flex: 1;

  & > div:first-of-type {
    flex: 1;
  }
`;

const identifierFieldStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: var(--surface-elevated);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-default);
`;

const identifierLabelStyle = css`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const identifierInputStyle = css`
  padding: 12px 20px;
  font-size: var(--text-xl);
  font-weight: 700;
  text-align: center;
  border: 2px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--input-bg);
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  min-width: 200px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-light, rgba(99, 102, 241, 0.2));
  }

  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }
`;

const identifierInputErrorStyle = css`
  border-color: var(--color-error);

  &:focus {
    border-color: var(--color-error);
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
  }
`;

function generatePlaceholderGrid(): GridValues {
  const rows = Array.from({ length: 3 }, () => {
    const indices = [0, 1, 2, 3, 4, 5, 6, 7, 8];
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = indices[i]!;
      indices[i] = indices[j]!;
      indices[j] = tmp;
    }
    const filled = new Set(indices.slice(0, 5));
    return Array.from({ length: 9 }, (_, i) => (filled.has(i) ? -1 : null)) as GridValues[number];
  });
  return rows as GridValues;
}

function getOverlayStyles(status: GridStatus) {
  switch (status) {
    case "WaitingForUpload":
    case "Uploaded":
      return [bingoGridOverlay, bingoGridOverlayBlur];
    case "NumbersExtracted":
      return [bingoGridOverlay, bingoGridOverlayDim];
    default:
      return null;
  }
}

export function getColumnRange(col: number): [number, number] {
  if (col === 0) return [1, 9];
  if (col === 8) return [80, 90];
  return [col * 10, col * 10 + 9];
}

export function validateGrid(
  numbers: GridValues,
  t: TFunction,
): {
  errors: string[];
  cellErrors: CellErrors;
  rowErrors: boolean[];
} {
  const errors: string[] = [];
  const cellErrors: CellErrors = numbers.map((row) => row.map(() => false));
  const rowErrors: boolean[] = [false, false, false];

  // Check each row has exactly 5 values
  for (let r = 0; r < 3; r++) {
    const filledCount = numbers[r]!.filter((c) => c !== null).length;
    if (filledCount !== 5) {
      errors.push(t("gridValidation.rowError", { row: r + 1, count: filledCount }));
      rowErrors[r] = true;
    }
  }

  // Check each cell: value between 1-90, and in the right decade for its column
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 9; c++) {
      const row = numbers[r]!;
      const value = row[c];
      if (value === null || value === undefined) continue;

      if (value < 1 || value > 90 || !Number.isInteger(value)) {
        cellErrors[r]![c] = true;
        errors.push(t("gridValidation.invalidNumber", { row: r + 1, col: c + 1, value }));
        continue;
      }

      const [min, max] = getColumnRange(c);
      if (value < min || value > max) {
        cellErrors[r]![c] = true;
        errors.push(t("gridValidation.outOfRange", { row: r + 1, col: c + 1, value, min, max }));
      }
    }
  }

  return { errors, cellErrors, rowErrors };
}

type DuplicateState = {
  existingGridId: string;
} | null;

export function ValidateGridModal({
  grid,
  onClose,
  onVerified,
}: {
  grid: GridType & { numbers: GridValues; identifier: string };
  onClose: () => void;
  onVerified?: () => void;
}) {
  const { t } = useTranslation();
  const [editedNumbers, setEditedNumbers] = useState<GridValues>(grid.numbers);
  const [editedIdentifier, setEditedIdentifier] = useState<string>(grid.identifier);
  const [identifierTouched, setIdentifierTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateState>(null);
  const { errors, cellErrors, rowErrors } = useMemo(
    () => validateGrid(editedNumbers, t),
    [editedNumbers, t],
  );

  const identifierError = editedIdentifier.trim() === "" ? t("gridValidation.identifierRequired") : null;
  const hasNumberChanges = JSON.stringify(editedNumbers) !== JSON.stringify(grid.numbers);
  const hasIdentifierChanges = editedIdentifier !== grid.identifier;
  const hasValidationErrors = errors.length > 0 || identifierError !== null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };

    // Lock body scroll when modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose, submitting]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setDuplicate(null);
    try {
      await trpcClient.verifyGrid.mutate({
        gridId: grid.id,
        ...(hasNumberChanges ? { numbers: editedNumbers } : {}),
        ...(hasIdentifierChanges ? { identifier: editedIdentifier } : {}),
      });
      onClose();
      onVerified?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to confirm grid";
      // Check for duplicate identifier error: "DUPLICATE_IDENTIFIER:<gridId>"
      const match = message.match(/DUPLICATE_IDENTIFIER:(\S+)/);
      if (match) {
        setDuplicate({ existingGridId: match[1]! });
      } else {
        setSubmitError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCurrentGrid = async () => {
    setSubmitting(true);
    try {
      await trpcClient.deleteGrid.mutate({ gridId: grid.id });
      onClose();
      onVerified?.();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to delete grid");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div css={validateGridModalOverlay} onClick={submitting ? undefined : onClose}>
      <div css={validateGridModal} onClick={(e) => e.stopPropagation()}>
        <h3 css={validateGridModalTitle}>{t("gridValidation.title")}</h3>

        {/* Identifier field */}
        <div css={identifierFieldStyle}>
          <label css={identifierLabelStyle} htmlFor="grid-identifier">
            {t("grids.identifier")}
          </label>
          <input
            id="grid-identifier"
            type="text"
            css={[identifierInputStyle, identifierTouched && identifierError && identifierInputErrorStyle]}
            value={editedIdentifier}
            onChange={(e) => setEditedIdentifier(e.target.value)}
            onBlur={() => setIdentifierTouched(true)}
            placeholder={t("grids.identifierPlaceholder")}
            disabled={submitting}
          />
          {identifierTouched && identifierError && (
            <span css={{ color: "var(--color-error)", fontSize: "var(--text-sm)" }}>
              {identifierError}
            </span>
          )}
        </div>

        {errors.length > 0 && (
          <div css={validateGridModalErrors}>
            {errors.map((error, i) => (
              <div key={i}>{error}</div>
            ))}
          </div>
        )}
        {submitError && (
          <div css={validateGridModalErrors}>
            <div>{submitError}</div>
          </div>
        )}
        {duplicate && (
          <div css={validateGridModalErrors}>
            <div>{t("gridValidation.duplicateIdentifier", { identifier: editedIdentifier })}</div>
          </div>
        )}
        <div css={validateGridModalContent}>
          <div css={validateGridModalPhoto}>
            <img
              src={`${IMAGE_BASE_URL}/${grid.fileUrl}`}
              alt={t("gridValidation.gridPhoto")}
              css={validateGridModalImg}
            />
          </div>
          <div css={[validateGridModalGrid, validateGridModalGridInner]}>
            <GridDisplay
              numbers={editedNumbers}
              onChange={setEditedNumbers}
              cellErrors={cellErrors}
              rowErrors={rowErrors}
            />
          </div>
        </div>
        <div css={validateGridModalActions}>
          <Button onClick={onClose} variant="secondary" disabled={submitting}>
            {t("common.close")}
          </Button>
          {duplicate ? (
            <Button onClick={handleDeleteCurrentGrid} variant="danger" disabled={submitting}>
              {submitting ? t("common.deleting") : t("gridValidation.deleteCurrentGrid")}
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              variant="primary"
              disabled={hasValidationErrors || submitting}
            >
              {submitting ? t("gridValidation.confirming") : t("common.confirm")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function Grid({ grid, onVerified }: { grid: GridType; onVerified?: () => void }) {
  const { t } = useTranslation();
  const rows = useMemo(() => grid.numbers ?? generatePlaceholderGrid(), [grid.numbers, grid.id]);
  const isPlaceholder = grid.numbers === null;
  const overlayStyles = getOverlayStyles(grid.status);
  const [showValidateModal, setShowValidateModal] = useState(false);

  return (
    <>
      <div css={bingoGrid}>
        {overlayStyles && <div css={overlayStyles} />}
        {grid.status === "NumbersExtracted" && (
          <Button
            variant="primary"
            size="xl"
            css={bingoGridValidateBtn}
            onClick={() => setShowValidateModal(true)}
          >
            {t("gridValidation.verifyButton")}
          </Button>
        )}
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} css={bingoRow}>
            {row.map((cell, cellIndex) => {
              const isEmpty = cell === null;
              return (
                <div key={cellIndex} css={[bingoCell, isEmpty && bingoCellEmpty]}>
                  {isEmpty ? "" : isPlaceholder ? "?" : cell}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {showValidateModal && grid.numbers && (
        <ValidateGridModal
          grid={grid as GridType & { numbers: GridValues }}
          onClose={() => setShowValidateModal(false)}
          onVerified={onVerified}
        />
      )}
    </>
  );
}
